import { Component, ElementRef, input, OnDestroy, OnInit, output, signal, Self, ViewChild, effect } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown, faChevronUp, faClose, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { BehaviorSubject, Observable, of, ReplaySubject, Subscription, switchMap } from 'rxjs';

import { Option } from '../../../models/option.model';

@Component({
  selector: 'eiq-autocomplete',
  templateUrl: 'autocomplete.component.html',
  styleUrls: ['autocomplete.component.scss', '_autocomplete-theme.component.scss']
})
export class AutocompleteComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  defaultOption = input<string>();
  hint = input<string>();
  label = input<string>();
  multiple = input<boolean>();
  options = input<Option<unknown>[]>();
  placeholder = input<string>();
  prefixIcon = input<IconDefinition>();
  searchPlaceholder = input<string>('Search...');
  testId = input<string>();

  optionSelected = output<unknown>();

  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faClose = faClose;
  faMagnifyingGlass = faMagnifyingGlass;
  disabled = signal(false);
  panelOpen$ = signal(false);

  filteredOptions$: Observable<Option<unknown>[]> = of([]);
  selectedOptions$ = signal<Option<unknown>[]>([]);

  control = new FormControl();

  onChange: (value: unknown) => void;
  onTouched: () => void;

  private input$ = new ReplaySubject<unknown>(1);
  private optionsEvent$ = new BehaviorSubject<string | null>(null);
  private subscriptions: Subscription[] = [];

  constructor(@Self() public ngControl: NgControl) {
    this.ngControl.valueAccessor = this;

    effect(
      () => {
        const options = this.options();
        if (!this.multiple() || !options?.length) {
          return;
        }

        const value = this.control?.value;
        if (!Array.isArray(value) || value.length === 0) {
          return;
        }

        const selected = value.map((v) => options.find((opt) => opt.value === v)).filter(Boolean);
        this.selectedOptions$.set(selected);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (!this.disabled()) {
          return;
        }

        this.panelOpen$.set(false);
        this.autocompleteTrigger?.closePanel();
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.control = this.ngControl.control as FormControl;
    this.disabled.set(this.control?.disabled ?? false);

    this.subscriptions.push(
      this.input$.subscribe((value: unknown) => {
        this.onTouched();
        this.onChange(value);
      })
    );

    this.filteredOptions$ = this.optionsEvent$.pipe(
      switchMap((filter: string) => {
        const allOptions = this.options() ?? [];
        const filtered = filter ? this.filter(filter) : allOptions;
        return of(filtered);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  displayFn = (optionValue: string): string => (optionValue ? this.options()?.find((opt) => opt.value === optionValue)?.text : '');

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: unknown): void {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      if (this.input?.nativeElement) {
        this.input.nativeElement.value = '';
      }
      if (this.multiple()) {
        this.selectedOptions$.set([]);
      }
      this.optionsEvent$.next(null);
      return;
    }

    if (this.multiple() && Array.isArray(value)) {
      const options = this.options() ?? [];
      const selected = value.map((v) => options.find((opt) => opt.value === v)).filter(Boolean);
      this.selectedOptions$.set(selected);
    }

    this.input$.next(value);
  }

  clearValue(): void {
    if (this.disabled()) {
      return;
    }

    if (this.input?.nativeElement) {
      this.input.nativeElement.value = '';
    }
    this.optionsEvent$.next(null);
    this.input$.next(null);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  filterWhenTyping(): void {
    if (this.disabled()) {
      return;
    }

    const filterValue = this.input.nativeElement.value.toLowerCase();
    this.optionsEvent$.next(filterValue);
  }

  onSearchInput(event: Event): void {
    if (this.disabled()) {
      return;
    }

    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
    this.optionsEvent$.next(filterValue);
  }

  isSelected(option: Option<unknown>): boolean {
    return this.selectedOptions$().some((opt) => opt.value === option.value);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.disabled()) {
      return;
    }

    const selectedValue = event.option.value;
    const current = this.selectedOptions$();
    const alreadySelected = current.some((opt) => opt.value === selectedValue);

    let updated: Option<unknown>[];
    if (alreadySelected) {
      updated = current.filter((opt) => opt.value !== selectedValue);
    } else {
      const option = this.options()?.find((opt) => opt.value === selectedValue);
      updated = option ? [...current, option] : current;
    }

    this.selectedOptions$.set(updated);
    this.input$.next(updated.map((o) => o.value));

    this.input.nativeElement.value = '';
    this.optionsEvent$.next(null);
  }

  removeOption(optionToRemove: Option<unknown>): void {
    if (this.disabled()) {
      return;
    }

    const updated = this.selectedOptions$().filter((opt) => opt.value !== optionToRemove.value);
    this.selectedOptions$.set(updated);
    this.input$.next(updated.map((o) => o.value));
    this.optionsEvent$.next(null);
  }

  private filter(value: string): Option<unknown>[] {
    return (this.options() ?? []).filter((opt) => {
      return opt.text.toLowerCase().includes(value.toLowerCase());
    });
  }
}
